import { ComponentType } from "react";

import AnticipationGuideOrganizer from "./AnticipationGuideOrganizer";
import CompareContrastOrganizer from "./CompareContrastOrganizer";
import ContextCluesDetective from "./ContextCluesDetective";
import FrayerModelOrganizer from "./FrayerModelOrganizer";
import KnowWonderLearnChart from "./KnowWonderLearnChart";
import MainIdeaOrganizer, { type MainIdeaFormData } from "./MainIdeaOrganizer";
import anticipationThumbnail from "./thumbnails/anticipation.png";
import compareContrastThumbnail from "./thumbnails/compare-contrast.png";
import contextCluesThumbnail from "./thumbnails/context-clues.png";
import frayerThumbnail from "./thumbnails/frayer.png";
import kwlThumbnail from "./thumbnails/kwl.png";
import mainIdeaThumbnail from "./thumbnails/main-idea.png";

import type { ExportablesGenerationResponse } from "@/types/models/Exportables.interface";

// Interface for template components - will be extended as needed for other template types
export interface TemplateProps<T = any> {
  formData?: MainIdeaFormData | T | null;
  onChange?: (data: Partial<T>) => void;
  title?: string;
  editMode?: boolean; // Optional prop to enable edit mode for the template
  isStudent?: boolean;
}
// Define more specific types for our registry
export interface TemplateDefinition<T> {
  Component: ComponentType<TemplateProps<T>>;
  // These can be updated once PDF export is implemented
  pdfOptions: {
    filename: string; // Default filename for exported PDF
    orientation: "portrait" | "landscape"; // Orientation for PDF export
  };
  title: string; // Title for the template UI
  label?: string; // Optional label for the template, used in UI when a shorter name is needed
  thumbnail?: string; // URL or import for thumbnail image located in src/components/AIComponents/ExportablesModal/templates/thumbnails
  // Unique key for SETI exportable item
  seti_identifier: keyof ExportablesGenerationResponse;
}

export interface TemplateRegistry {
  AnticipationGuide: TemplateDefinition<any>;
  CentralIdeaAndDetails: TemplateDefinition<any>;
  CompareAndContrast: TemplateDefinition<any>;
  ContextCluesDetective: TemplateDefinition<any>;
  FrayerModel: TemplateDefinition<any>;
  KWL: TemplateDefinition<any>;
  // Add more templates here as they are created
}

// Template registry - maps template names to component types

export const TEMPLATE_REGISTRY: TemplateRegistry = {
  AnticipationGuide: {
    Component: AnticipationGuideOrganizer,
    pdfOptions: {
      filename: "anticipation-guide.pdf",
      orientation: "landscape",
    },
    thumbnail: anticipationThumbnail.src,
    title: "Anticipation guide",
    seti_identifier: "anticipation_guide",
  },
  CentralIdeaAndDetails: {
    Component: MainIdeaOrganizer,
    pdfOptions: {
      filename: "central-idea-and-details.pdf",
      orientation: "landscape",
    },
    thumbnail: mainIdeaThumbnail.src,
    title: "Central idea and details",
    label: "Central idea",
    seti_identifier: "main_idea_key_details",
  },
  CompareAndContrast: {
    Component: CompareContrastOrganizer,
    pdfOptions: {
      filename: "compare-and-contrast.pdf",
      orientation: "landscape",
    },
    thumbnail: compareContrastThumbnail.src,
    title: "Compare & contrast",
    seti_identifier: "compare_contrast",
  },
  ContextCluesDetective: {
    Component: ContextCluesDetective,
    pdfOptions: {
      filename: "context-clues-worksheet.pdf",
      orientation: "landscape",
    },
    thumbnail: contextCluesThumbnail.src,
    title: "Context clues worksheet",
    label: "Context clues",
    seti_identifier: "context_clues",
  },
  FrayerModel: {
    Component: FrayerModelOrganizer,
    pdfOptions: {
      filename: "frayer-model.pdf",
      orientation: "landscape",
    },
    thumbnail: frayerThumbnail.src,
    title: "Building word knowledge with Frayer model",
    label: "Frayer model",
    seti_identifier: "frayer_model",
  },
  KWL: {
    Component: KnowWonderLearnChart,
    pdfOptions: {
      filename: "kwl-chart.pdf",
      orientation: "landscape",
    },
    thumbnail: kwlThumbnail.src,
    title: "KWL Chart",
    seti_identifier: "know_wonder_learn",
  },
};
